import {
  AfterViewInit,
  Component,
  ElementRef,
  EventEmitter,
  Input,
  OnDestroy,
  Output,
  ViewChild,
} from '@angular/core';
import { LoadingController } from 'ionic-angular';
import { ServerService } from '../../services/server-service';
import { ToastService } from '../../services/toast-service';
import {
  DEFAULT_APP_QUESTIONNAIRE_QUESTIONS,
  DEFAULT_OPEN_FEEDBACK,
  QuestionnaireItem,
} from './app-questionnaire.model';

declare const lottie: any;

@Component({
  selector: 'app-questionnaire',
  templateUrl: 'app-questionnaire.html',
})
export class AppQuestionnaireComponent implements AfterViewInit, OnDestroy {
  @Input() submitEndpoint: string = 'forms/submitAppQuestionnaire.php';
  @Input() submittedStorageKey: string = 'appQuestionnaireSubmittedV1';
  @Input() title: string = 'שאלון משוב על האפליקציה';
  @Input() subtitle: string = 'דרגו כל שאלה מ-1 עד 5 כוכבים. המשוב שלכם עוזר לנו לשפר את החוויה.';
  @Input() mandatoryNotice: string =
    'יש לדרג את כל השאלות (1–5 כוכבים). שדה המלל למטה הוא אופציונלי.';
  @Input() lottiePath: string = 'assets/lottie/questionnaire-survey.json';
  @Input() questions: QuestionnaireItem[] = DEFAULT_APP_QUESTIONNAIRE_QUESTIONS;
  @Input() openFeedbackTitle: string = DEFAULT_OPEN_FEEDBACK.title;
  @Input() openFeedbackPlaceholder: string = DEFAULT_OPEN_FEEDBACK.placeholder;

  @Output() submitted = new EventEmitter<any>();
  @Output() closed = new EventEmitter<void>();

  @ViewChild('questionnaireLottie') questionnaireLottie: ElementRef;

  readonly stars: number[] = [1, 2, 3, 4, 5];
  ratings: { [key: string]: number } = {};
  suggestedChange: string = '';
  submitting: boolean = false;

  private anim: any;

  constructor(
    private api: ServerService,
    private toast: ToastService,
    public loadingCtrl: LoadingController,
  ) { }

  ngAfterViewInit() {
    this.initLottie();
  }

  ngOnDestroy() {
    this.destroyLottie();
  }

  setRating(key: string, value: number): void {
    this.ratings[key] = value;
  }

  isRated(key: string): boolean {
    const value = this.ratings[key];
    return typeof value === 'number' && value >= 1 && value <= 5;
  }

  allRatingsComplete(): boolean {
    return this.questions.every((q) => this.isRated(q.key));
  }

  allFieldsComplete(): boolean {
    return this.allRatingsComplete();
  }

  close(): void {
    this.closed.emit();
  }

  submitQuestionnaire(): void {
    if (!this.allRatingsComplete()) {
      this.toast.presentToast('יש לדרג את כל השאלות (1–5 כוכבים)');
      return;
    }

    const object = {
      personalID: localStorage.getItem('user_id'),
      mercazAllId: localStorage.getItem('mercazAllId'),
      pqpq: localStorage.getItem('server'),
      pushToken: localStorage.getItem('push'),
      dailyProductivity: this.ratings['dailyProductivity'],
      uiIntuitive: this.ratings['uiIntuitive'],
      stabilitySpeed: this.ratings['stabilitySpeed'],
      functionalityNeeds: this.ratings['functionalityNeeds'],
      recommendToPeer: this.ratings['recommendToPeer'],
      suggestedChange: (this.suggestedChange || '').trim(),
    };

    const loading = this.loadingCtrl.create({
      content: 'שולח משוב...',
      spinner: 'bubbles',
    });
    this.submitting = true;
    loading.present();

    this.api.sendPostParams(this.submitEndpoint, object).then((data) => {
      loading.dismiss();
      this.submitting = false;

      if (data && data['sucsses']) {
        try {
          localStorage.setItem(this.submittedStorageKey, '1');
        } catch (e) { /* noop */ }
        this.toast.presentToast('תודה! המשוב נשלח בהצלחה');
        this.submitted.emit({ data, payload: object });
        return;
      }

      this.toast.presentToast((data && data['error']) || 'שגיאת מערכת, יש לנסות שוב');
    }, () => {
      loading.dismiss();
      this.submitting = false;
      this.toast.presentToast('שגיאת תקשורת, יש לנסות שוב');
    });
  }

  private initLottie(retryCount: number = 0) {
    const container = this.questionnaireLottie && this.questionnaireLottie.nativeElement;
    if (!container) {
      if (retryCount < 10) {
        setTimeout(() => this.initLottie(retryCount + 1), 200);
      }
      return;
    }

    if (typeof lottie === 'undefined') {
      if (retryCount < 20) {
        setTimeout(() => this.initLottie(retryCount + 1), 200);
      }
      return;
    }

    if (this.anim) {
      return;
    }

    try {
      this.anim = lottie.loadAnimation({
        container: container,
        renderer: 'svg',
        loop: true,
        autoplay: true,
        path: this.lottiePath,
      });
      this.anim.addEventListener('data_failed', (err: any) => {
        console.log('questionnaire lottie data_failed for', this.lottiePath, err);
      });
    } catch (e) {
      console.log('questionnaire lottie init error', e);
    }
  }

  private destroyLottie() {
    if (this.anim) {
      try { this.anim.destroy(); } catch (e) { /* noop */ }
      this.anim = null;
    }
  }
}



// WEBPACK FOOTER //
// ./src/components/app-questionnaire/app-questionnaire.ts